import { Controller } from '@hotwired/stimulus'

export default class extends Controller
{
  static targets = [
    "address",
    "city",
    "state",
    "phone",
    "phoneCountryCode",
    "postalCode",
    "country",
    "statusOfChanges",
    "googleMapsResult"
  ];

  static values = {
    lockedCountryCode: String
  };

  initialize() {
    this.placeChanged = this.placeChanged.bind(this);
  }

  connect() {
    if (typeof google !== "undefined" && google.maps && google.maps.places) {
      this.initAutocomplete();
    } else {
      this._onMapsReady = () => {
        if (typeof google !== "undefined" && google.maps && google.maps.places) {
          this.initAutocomplete();
          if (this.hasLockedCountryCodeValue && this.lockedCountryCodeValue !== "") {
            this.restrictCountryGoogle(this.lockedCountryCodeValue);
          } else if (this.hasCountryTarget) {
            var opt = this.countryTarget.selectedOptions[0];
            if (opt && opt.dataset.code !== undefined) this.restrictCountryGoogle(opt.dataset.code);
          }
        }
      };
      window.addEventListener("google-maps-callback", this._onMapsReady, { once: true });
    }

    if (typeof window.initAddressPhone === 'function') {
      var phoneInput = this.hasPhoneTarget ? this.phoneTarget : null;
      var countryCodeInput = this.hasPhoneCountryCodeTarget ? this.phoneCountryCodeTarget : null;
      window.initAddressPhone(phoneInput, countryCodeInput);
    }

    // When the country is fixed (read-only admin flow), pin Google autocomplete
    // and the phone input to that country. No <select> is present.
    // Runs AFTER initAddressPhone so window.iti is already initialized.
    if (this.hasLockedCountryCodeValue && this.lockedCountryCodeValue !== "") {
      this.restrictCountryGoogle(this.lockedCountryCodeValue);
      var iti = this._iti();
      if (iti) iti.setCountry(this.lockedCountryCodeValue);
    }

    if (this.hasCountryTarget) {
      // Only restrict Google autocomplete on initial load, not the phone country
      var selectedOption = this.countryTarget.selectedOptions[0];
      if (selectedOption && selectedOption.dataset.code !== undefined) {
        this.restrictCountryGoogle(selectedOption.dataset.code);
      }
      // select2 hides the native select; relay its pick as a change. Layouts without jQuery
      // (the shop) keep the plain select, which fires change itself.
      if (typeof $ !== 'undefined') {
        $(this.countryTarget).on('select2:select', function () {
          let event = new Event('change', { bubbles: true }) // fire a native event
          this.dispatchEvent(event);
        });
      }
    }
  }

  disconnect() {
    if (this._onMapsReady) {
      window.removeEventListener("google-maps-callback", this._onMapsReady);
      this._onMapsReady = null;
    }
  }

  initAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(this.addressTarget, this.autocompleteOptions);
    this.autocomplete.addListener('place_changed', this.placeChanged.bind(this));
  }

  placeChanged() {
    this.place = this.autocomplete.getPlace();
    const addressComponents = this.place.address_components;

    if (addressComponents !== void 0) {
      const formattedAddress = this.formatAddressComponents(addressComponents);
      this.setAddressComponents(formattedAddress);
    }
  }

  setAddressComponents (address) {
    if (this.hasCityTarget) this.setValue(this.cityTarget, address.locality || "N/A");
    if (this.hasStateTarget) this.setValue(this.stateTarget, address.administrative_area_level_1 || "N/A");
    if (this.hasPostalCodeTarget) this.setValue(this.postalCodeTarget, address.postal_code || "N/A");
    if (this.hasAddressTarget) {
      let full_address = this.addressTarget.value.split(',');

      if (full_address.length >= 3) {
        full_address.splice(-2);
      }
      else if (full_address.length === 2) {
        if (isNaN(full_address[1])) {
          full_address.splice(-1);
        }
      }

      // `split` yields an array; join it back so the guard in `setValue` compares like with like.
      this.setValue(this.addressTarget, full_address.join(','), { silent: true });
    }
    if (this.hasStatusOfChangesTarget) this.statusOfChangesTarget.value = 'unchanged';
    if (this.hasGoogleMapsResultTarget) this.googleMapsResultTarget.value = Object.entries(address).map(([key, value]) => `${key}=${value}`).join(';');
  }

  // Picking an address from Google Places fills these fields programmatically, and a plain
  // `.value =` assignment fires no event. Any controller gating on `input`/`change` (the gift
  // card submit button, for one) would never learn the field stopped being empty, leaving the
  // form visibly complete but the button disabled.
  //
  // `silent` skips the `input` event for the field the Places widget owns: it listens for `input`
  // on that field, so echoing one back reads as fresh typing and reopens the suggestions dropdown
  // right after a selection. `change` is safe — the widget does not act on it.
  setValue (target, value, { silent = false } = {}) {
    if (target.value === value) return;

    target.value = value;
    if (!silent) target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  get autocompleteOptions() {
    return {
      fields: ['address_components']
    }
  }

  preventSubmit(event) {
    if (event.key === "Enter") {
      event.preventDefault();
    }
    if (this.hasStatusOfChangesTarget) this.statusOfChangesTarget.value = 'changed';
  }

  formatAddressComponents(addressComponents) {
    const data = {};

    addressComponents.forEach((component) => {
      const type = component.types[0];
      data[type] = component.long_name;
    });

    return data;
  }

  restrictCountry() {
    let country_code = this.countryTarget.selectedOptions[0].dataset.code;

    if (country_code !== undefined) {
      this.restrictCountryGoogle(country_code);
      var iti = this._iti();
      if (iti) iti.setCountry(country_code);
    }
  }

  _iti() {
    if (this.hasPhoneTarget && this.phoneTarget.__iti) return this.phoneTarget.__iti;
    return typeof window.iti !== 'undefined' ? window.iti : null;
  }

  restrictCountryGoogle(countrycode) {
    if (this.autocomplete && typeof this.autocomplete.setComponentRestrictions === 'function') {
      this.autocomplete.setComponentRestrictions({country: countrycode});
    }
  }

  lockedCountryCodeValueChanged(value) {
    if (!value) return;
    this.restrictCountryGoogle(value);
    var iti = this._iti();
    if (iti) iti.setCountry(value);
  }

  cleanupForm() {
    if (this.hasCityTarget) this.cityTarget.value = "";
    if (this.hasPhoneTarget) this.phoneTarget.value = "";
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = "";
    if (this.hasAddressTarget) {
      this.addressTarget.value = "";
      this.addressTarget.focus();
    }
  }
}

// export { GooglePlacesAutocomplete as default };

window.initTest = function () {
  // API library requires callback function to exist.
}


window.initAutocomplete = function(...args) {
  const event = document.createEvent("Events")
  event.initEvent("google-maps-callback", true, true)
  event.args = args
  window.dispatchEvent(event)
}
